import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, Pause, Play, Trash2, Zap } from 'lucide-react';
import {
  Button,
  ConfirmDialog,
  Input,
  Modal,
  Select,
  Textarea,
  toast,
} from '../../components/ui';
import { api } from '../../lib/api';
import { cn } from '../../lib/cn';
import { formatDate, formatMoney } from '../../lib/format';
import { useInvoiceClients } from './api';
import { ScheduleStripePanel } from './stripe-link';
import {
  CADENCE_OPTIONS,
  useCreateRecurring,
  useDeleteRecurring,
  useGenerateRecurring,
  useSubjectPreview,
  useUpdateRecurring,
  type Cadence,
  type RecurringTemplate,
} from './recurring-api';

/**
 * The whole form is the sheet's four columns — client, subject, amount, next
 * invoice date — plus one pre-filled dropdown for the cadence, because the
 * sheet prints it in the same cell ("07/01/2026 (every month)").
 *
 * Everything else lives under "Optional detail" and has a working default:
 * net 30, kind `retainer`, no note. A field you must fill in is a
 * step you have added; five of those is a form nobody opens seventeen times.
 */

const KIND_OPTIONS = [
  { value: 'retainer', label: 'Retainer' },
  { value: 'hosting', label: 'Hosting' },
  { value: 'support', label: 'Support' },
  { value: 'service', label: 'Service' },
  { value: 'other', label: 'Other' },
];

interface FormState {
  clientId: string;
  subject: string;
  amount: string;
  nextIssueDate: string;
  cadence: Cadence;
  agreementId: string;
  kind: string;
  netDays: string;
  note: string;
  autoSend: boolean;
}

function blank(today: string): FormState {
  return {
    clientId: '',
    subject: '',
    amount: '',
    // The 1st of next month is what a monthly retainer almost always wants.
    nextIssueDate: firstOfNextMonth(today),
    cadence: 'monthly',
    agreementId: '',
    kind: 'retainer',
    netDays: '30',
    note: '',
    autoSend: false,
  };
}

function firstOfNextMonth(today: string): string {
  const [y, m] = today.split('-').map(Number);
  const year = m === 12 ? y + 1 : y;
  const month = m === 12 ? 1 : m + 1;
  return `${year}-${String(month).padStart(2, '0')}-01`;
}

function toFormState(t: RecurringTemplate): FormState {
  return {
    clientId: t.clientId,
    subject: t.subject,
    amount: String(t.amount),
    nextIssueDate: t.nextIssueDate,
    cadence: t.cadence,
    agreementId: t.agreementId ?? '',
    kind: t.kind,
    netDays: String(t.netDays),
    note: t.note ?? '',
    autoSend: t.autoSend,
  };
}

function useDebounced<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(timer);
  }, [value, ms]);
  return debounced;
}

/** Active agreements for the chosen client — only fetched once one is chosen. */
function useClientAgreements(clientId: string) {
  return useQuery({
    queryKey: ['agreements', 'options', clientId],
    queryFn: () =>
      api.get<{ agreements: Array<{ id: string; name: string }> }>('/agreements', {
        params: { clientId, active: 'true' },
      }),
    enabled: Boolean(clientId),
    staleTime: 5 * 60_000,
  });
}

export function RecurringFormModal({
  open,
  template,
  today,
  onOpenChange,
}: {
  open: boolean;
  /** null = create a new schedule. */
  template: RecurringTemplate | null;
  today: string;
  onOpenChange: (open: boolean) => void;
}) {
  const clients = useInvoiceClients();
  const create = useCreateRecurring();
  const update = useUpdateRecurring();
  const remove = useDeleteRecurring();
  const generate = useGenerateRecurring();

  const [form, setForm] = useState<FormState>(() => blank(today));
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [showDetail, setShowDetail] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(template ? toFormState(template) : blank(today));
    setErrors({});
    setShowDetail(false);
  }, [open, template, today]);

  const agreements = useClientAgreements(form.clientId);
  const netDaysNum = Number(form.netDays) || 30;
  // Debounced: the preview is resolved server-side (one resolver, not two), and
  // a request per keystroke would be forty requests to type one subject.
  const debouncedSubject = useDebounced(form.subject, 250);
  const preview = useSubjectPreview(debouncedSubject, form.nextIssueDate, netDaysNum);

  const clientOptions = useMemo(
    () => (clients.data ?? []).map((c) => ({ value: c.id, label: c.name })),
    [clients.data],
  );
  const agreementOptions = useMemo(
    () => [
      { value: '', label: 'No agreement' },
      ...(agreements.data?.agreements ?? []).map((p) => ({ value: p.id, label: p.name })),
    ],
    [agreements.data],
  );

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const amountNum = Number(form.amount);
  const pending = create.isPending || update.isPending;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const next: typeof errors = {};
    if (!form.clientId) next.clientId = 'Pick a client';
    if (!form.subject.trim()) next.subject = 'What is this invoice for?';
    if (!form.amount || !Number.isFinite(amountNum) || amountNum <= 0) {
      next.amount = 'Enter an amount above zero';
    }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(form.nextIssueDate)) next.nextIssueDate = 'Pick a date';
    if (Object.keys(next).length) {
      setErrors(next);
      return;
    }

    const payload = {
      clientId: form.clientId,
      subject: form.subject.trim(),
      amount: amountNum,
      nextIssueDate: form.nextIssueDate,
      cadence: form.cadence,
      agreementId: form.agreementId || null,
      kind: form.kind,
      netDays: netDaysNum,
      note: form.note.trim() || null,
      autoSend: form.autoSend,
    };

    const done = (message: string) => () => {
      toast.success(message);
      onOpenChange(false);
    };
    const failed = (fallback: string) => (error: unknown) =>
      toast.error(error instanceof Error ? error.message : fallback);

    if (template) {
      update.mutate(
        { id: template.id, ...payload },
        { onSuccess: done('Schedule updated'), onError: failed('Could not update the schedule') },
      );
    } else {
      create.mutate(payload, {
        onSuccess: done('Schedule created'),
        onError: failed('Could not create the schedule'),
      });
    }
  };

  const unknown = preview.data?.unknownVariables ?? [];

  return (
    <>
      <Modal
        open={open}
        onOpenChange={onOpenChange}
        title={template ? 'Edit recurring schedule' : 'New recurring schedule'}
        description={
          template?.stripe
            ? form.autoSend
              ? 'Paid by a Stripe subscription: the invoice is emailed on its date, and the charge marks it paid and sends the receipt.'
              : 'Paid by a Stripe subscription: each charge issues the invoice, marks it paid and emails the receipt.'
            : form.autoSend
              ? 'It issues the invoice on schedule and emails it to the client — see Send automatically below.'
              : 'It drafts the invoice on schedule. Nothing is sent until you send it — or switch on Send automatically below.'
        }
      >
        <form onSubmit={submit} className="space-y-4">
          {/* The row's two actions, repeated here because below 640px the
              table hides its action column and this modal IS the row. */}
          {template && (
            <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-grey-200 bg-grey-50 px-3 py-2">
              <span className="text-caption text-grey-600">
                Next invoice{' '}
                <span className="num font-semibold text-secondary">
                  {formatDate(template.nextIssueDate)}
                </span>
                {template.lastIssuedAt && (
                  <> · last generated {formatDate(template.lastIssuedAt.slice(0, 10))}</>
                )}
              </span>
              <div className="flex gap-1">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  loading={generate.isPending}
                  onClick={() =>
                    generate.mutate(template.id, {
                      onSuccess: (invoice) => toast.success(`Draft #${invoice.number} created`),
                      onError: (error) =>
                        toast.error(
                          error instanceof Error ? error.message : 'Could not generate the draft',
                        ),
                    })
                  }
                >
                  <Zap className="h-4 w-4" aria-hidden />
                  Generate now
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() =>
                    update.mutate(
                      { id: template.id, isActive: !template.isActive },
                      {
                        onSuccess: () =>
                          toast.success(template.isActive ? 'Schedule paused' : 'Schedule resumed'),
                        onError: (error) =>
                          toast.error(
                            error instanceof Error ? error.message : 'Could not update the schedule',
                          ),
                      },
                    )
                  }
                >
                  {template.isActive ? (
                    <Pause className="h-4 w-4" aria-hidden />
                  ) : (
                    <Play className="h-4 w-4" aria-hidden />
                  )}
                  {template.isActive ? 'Pause' : 'Resume'}
                </Button>
              </div>
            </div>
          )}

          {template && <ScheduleStripePanel template={template} />}

          <Select
            label="Client"
            options={clientOptions}
            placeholder={clients.isLoading ? 'Loading clients…' : 'Choose a client'}
            value={form.clientId}
            onChange={(e) => {
              set('clientId', e.target.value);
              // An agreement belongs to one client; keeping the old one would
              // silently bill the wrong agreement.
              setForm((prev) => ({ ...prev, agreementId: '' }));
            }}
            error={errors.clientId}
          />

          {/* Out of "Optional detail" (Jesse, 2026-11-04): every invoice this
              schedule makes carries the agreement on its line, which is what
              counts it as invoiced ON the agreement. Hidden, it defaulted to
              nothing and monthly retainers read "$0 invoiced" on /agreements. */}
          <div>
            <Select
              label="Agreement"
              options={agreementOptions}
              value={form.agreementId}
              disabled={!form.clientId}
              onChange={(e) => set('agreementId', e.target.value)}
            />
            {form.clientId && !form.agreementId && (
              <p className="mt-1.5 text-caption text-status-partial">
                With no agreement, these invoices are not counted on any agreement.
              </p>
            )}
          </div>

          <div>
            <Input
              label="Invoice subject"
              value={form.subject}
              onChange={(e) => set('subject', e.target.value)}
              error={errors.subject}
              placeholder="Support Retainer — %invoice_issue_month_name% %invoice_issue_year%"
              maxLength={200}
            />
            {/* What the client will actually read, resolved by the server —
                the same resolver that writes the invoice. */}
            {form.subject.trim() && (
              <p className="mt-1.5 text-caption text-grey-600">
                Next invoice reads{' '}
                <span className="font-semibold text-secondary">
                  “{preview.data?.subject ?? form.subject}”
                </span>
              </p>
            )}
            {unknown.length > 0 && (
              <p className="mt-1 text-caption text-status-late">
                Unknown variable{unknown.length > 1 ? 's' : ''}: {unknown.join(', ')} — left as
                typed on the invoice.
              </p>
            )}
            <VariableHelp onInsert={(token) => set('subject', `${form.subject}${token}`)} />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Input
              label="Amount"
              type="number"
              inputMode="decimal"
              min="0.01"
              step="0.01"
              value={form.amount}
              onChange={(e) => set('amount', e.target.value)}
              error={errors.amount}
              hint={
                Number.isFinite(amountNum) && amountNum > 0 ? formatMoney(amountNum) : undefined
              }
            />
            <Input
              label="Next invoice"
              type="date"
              value={form.nextIssueDate}
              onChange={(e) => set('nextIssueDate', e.target.value)}
              error={errors.nextIssueDate}
              hint={form.nextIssueDate <= today ? 'Due now — generate it whenever you like' : undefined}
            />
          </div>

          <Select
            label="Repeats"
            options={CADENCE_OPTIONS}
            value={form.cadence}
            onChange={(e) => set('cadence', e.target.value as Cadence)}
          />

          {/* Jesse, 2026-10-01: recurring invoices may go out without a human.
              Off by default, per schedule. 2026-11-02: on a Stripe-paid schedule
              the switch sends the invoice ON ITS DATE (a client's A/P wants it on
              the 1st) with no way to pay it by hand — the subscription pays it,
              and the charge settles that same invoice. See backend autopay.ts. */}
          <label
            className={cn(
              'flex cursor-pointer items-start gap-2.5 rounded-lg border px-3 py-2.5',
              form.autoSend ? 'border-accent/40 bg-accent/5' : 'border-grey-200 bg-grey-50',
            )}
          >
            <input
              type="checkbox"
              checked={form.autoSend}
              onChange={(e) => setForm((prev) => ({ ...prev, autoSend: e.target.checked }))}
              className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer rounded border-grey-300 accent-accent"
            />
            <span className="min-w-0">
              <span className="block text-body-sm font-semibold text-secondary">Send automatically</span>
              <span className="block text-caption text-grey-600">
                {template?.stripe
                  ? 'Paid by a Stripe subscription: on the invoice date at 9:00 AM the invoice is emailed to the billing contacts, saying it is paid automatically (no Pay button). When the charge clears it is marked paid and the receipt goes out. A card charged before 9:00 sends the paid invoice with its receipt instead.'
                  : 'On the invoice date at 9:00 AM, the invoice is emailed to the client’s billing contacts — no draft to send. If it can’t go, it waits as a draft and this schedule says why.'}
              </span>
            </span>
          </label>

          {/* Refinements. Everything above is the sheet's four columns; these
              only sharpen it, and every one of them already has an answer. */}
          <div className="rounded-lg border border-grey-200">
            <button
              type="button"
              aria-expanded={showDetail}
              onClick={() => setShowDetail((v) => !v)}
              className="flex w-full items-center justify-between gap-2 px-3 py-2 text-caption font-bold uppercase tracking-wider text-grey-500"
            >
              Optional detail
              <ChevronDown
                className={cn('h-4 w-4 transition-transform', showDetail && 'rotate-180')}
                aria-hidden
              />
            </button>
            {showDetail && (
              <div className="space-y-3 border-t border-grey-200 p-3">
                <div className="grid grid-cols-2 gap-3">
                  <Select
                    label="Kind"
                    options={KIND_OPTIONS}
                    value={form.kind}
                    onChange={(e) => set('kind', e.target.value)}
                  />
                  <Input
                    label="Payment terms (days)"
                    type="number"
                    inputMode="numeric"
                    min="0"
                    max="365"
                    step="1"
                    value={form.netDays}
                    onChange={(e) => set('netDays', e.target.value)}
                  />
                </div>
                <Textarea
                  label="Internal note"
                  rows={2}
                  maxLength={500}
                  value={form.note}
                  onChange={(e) => set('note', e.target.value)}
                  placeholder="Never printed on the invoice"
                />
              </div>
            )}
          </div>

          <div className="flex items-center justify-between gap-2 pt-2">
            {template ? (
              <Button
                type="button"
                variant="ghost"
                className="text-primary-600 hover:bg-primary-50"
                onClick={() => setConfirmDelete(true)}
              >
                <Trash2 className="h-4 w-4" aria-hidden />
                Delete
              </Button>
            ) : (
              <span />
            )}
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" loading={pending}>
                {template ? 'Save changes' : 'Create schedule'}
              </Button>
            </div>
          </div>
        </form>
      </Modal>

      <ConfirmDialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        title="Delete this schedule?"
        description={
          template
            ? `${template.client.name} · ${formatMoney(template.amount)} ${template.cadenceLabel}. Invoices it already created are untouched. Pausing it keeps the history and can be undone.`
            : undefined
        }
        confirmLabel="Delete schedule"
        destructive
        onConfirm={() =>
          remove.mutateAsync(template!.id).then(
            () => {
              toast.success('Schedule deleted');
              setConfirmDelete(false);
              onOpenChange(false);
            },
            (error: unknown) => {
              toast.error(error instanceof Error ? error.message : 'Could not delete the schedule');
              throw error;
            },
          )
        }
      />
    </>
  );
}

/**
 * The Harvest variables, one click each. They are the reason a subject can be
 * pasted straight out of Harvest and keep working; hiding them behind
 * documentation would mean retyping the month into seventeen subjects every
 * month, which is the job this feature exists to delete.
 */
function VariableHelp({ onInsert }: { onInsert: (token: string) => void }) {
  const [open, setOpen] = useState(false);
  const common = [
    { token: '%invoice_issue_month_name%', label: 'September' },
    { token: '%invoice_issue_year%', label: '2026' },
    { token: '%invoice_issue_month%', label: '09' },
    { token: '%invoice_issue_quarter%', label: '3' },
    { token: '%invoice_issue_previous_month_name%', label: 'August' },
    { token: '%invoice_issue_next_month_name%', label: 'October' },
  ];
  return (
    <div className="mt-1.5">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="text-caption font-semibold text-accent-700 hover:underline"
      >
        {open ? 'Hide date variables' : 'Use a date variable'}
      </button>
      {open && (
        <div className="mt-1.5 flex flex-wrap gap-1.5">
          {common.map((v) => (
            <button
              key={v.token}
              type="button"
              onClick={() => onInsert(v.token)}
              title={`Inserts “${v.label}” when the invoice is issued`}
              className="rounded-full border border-grey-200 bg-grey-50 px-2 py-0.5 text-caption text-grey-700 hover:border-accent hover:text-accent-700"
            >
              {v.token}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
